import React, { useEffect, useState } from 'react';
import { Card, Table, Tag, message, Space, Input, Select, DatePicker, Button } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { api } from '../services/api';
import dayjs from 'dayjs';
import { useBreakpoint } from '../hooks/useBreakpoint';

interface SmsLogItem {
  id: number;
  customerId: number;
  customerNo: string;
  serviceOrderId: number;
  serviceOrderNo: string;
  status: number;
  statusText: string;
  phone: string;
  message: string;
  sentAt: string;
  success: boolean;
  errorMessage?: string;
}

const STATUS_OPTIONS = [
  { value: 1, label: 'Kabul' },
  { value: 3, label: 'Onarımda' },
  { value: 5, label: 'Teslim Edildi' },
];

const SmsLogPage: React.FC = () => {
  const isMobile = useBreakpoint();
  const [items, setItems] = useState<SmsLogItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [customerNo, setCustomerNo] = useState<string>('');
  const [serviceOrderNo, setServiceOrderNo] = useState<string>('');
  const [status, setStatus] = useState<number | undefined>();
  const [fromDate, setFromDate] = useState<dayjs.Dayjs | null>(null);
  const [toDate, setToDate] = useState<dayjs.Dayjs | null>(null);

  const loadItems = async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = {};
      if (customerNo) params.customerNo = customerNo.trim();
      if (serviceOrderNo) params.serviceOrderNo = serviceOrderNo.trim();
      if (status !== undefined) params.status = String(status);
      if (fromDate) params.fromDate = fromDate.toISOString();
      if (toDate) params.toDate = toDate.toISOString();
      const { data } = await api.get<SmsLogItem[]>('/smslogs', { params });
      setItems(data ?? []);
    } catch (error: any) {
      message.error(error?.response?.data?.message || 'SMS log yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const columns = [
    {
      title: 'Müşteri No',
      dataIndex: 'customerNo',
      key: 'customerNo',
      width: 100,
    },
    {
      title: 'Fiş No',
      dataIndex: 'serviceOrderNo',
      key: 'serviceOrderNo',
      width: 120,
    },
    {
      title: 'Fiş Durumu',
      dataIndex: 'statusText',
      key: 'statusText',
      width: 110,
      render: (text: string) => <Tag>{text}</Tag>,
    },
    {
      title: 'Telefon',
      dataIndex: 'phone',
      key: 'phone',
      width: 120,
    },
    {
      title: 'Gönderilen Mesaj',
      dataIndex: 'message',
      key: 'message',
      ellipsis: true,
    },
    {
      title: 'Tarih',
      dataIndex: 'sentAt',
      key: 'sentAt',
      width: 160,
      render: (d: string) => dayjs(d).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: 'Durum',
      dataIndex: 'success',
      key: 'success',
      width: 90,
      render: (ok: boolean, r: SmsLogItem) =>
        ok ? (
          <Tag color="green">Başarılı</Tag>
        ) : (
          <Tag color="red" title={r.errorMessage}>
            Hata
          </Tag>
        ),
    },
  ];

  return (
    <div>
      <Card title="SMS Log">
        <Space wrap style={{ marginBottom: 16 }}>
          <Input
            placeholder="Müşteri No"
            value={customerNo}
            onChange={(e) => setCustomerNo(e.target.value)}
            style={{ width: 120 }}
            allowClear
          />
          <Input
            placeholder="Fiş No (örn: SRV-20260214-0001)"
            value={serviceOrderNo}
            onChange={(e) => setServiceOrderNo(e.target.value)}
            style={{ width: 200 }}
            allowClear
          />
          <Select
            placeholder="Fiş Durumu"
            value={status}
            onChange={setStatus}
            style={{ width: 130 }}
            allowClear
            options={STATUS_OPTIONS}
          />
          <DatePicker
            placeholder="Başlangıç"
            value={fromDate}
            onChange={setFromDate}
            format="DD.MM.YYYY"
          />
          <DatePicker
            placeholder="Bitiş"
            value={toDate}
            onChange={setToDate}
            format="DD.MM.YYYY"
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={loadItems}>
            Ara
          </Button>
        </Space>
        <Table
          columns={columns}
          dataSource={items}
          rowKey="id"
          loading={loading}
          scroll={{ x: 700 }}
          pagination={{ pageSize: 20, showSizeChanger: !isMobile }}
          size="small"
        />
      </Card>
    </div>
  );
};

export default SmsLogPage;
